import { Injectable } from '@angular/core'
import { Apollo } from 'apollo-angular'
import { HttpLink } from 'apollo-angular-link-http'
import { HttpHeaders } from '@angular/common/http'
import { InMemoryCache, InMemoryCacheConfig } from 'apollo-cache-inmemory/lib/inMemoryCache'
import { IntrospectionFragmentMatcher } from 'apollo-cache-inmemory'
import { onError } from 'apollo-link-error'
import { ApolloLink } from 'apollo-link'
import { environment } from '../environments/environment'
import { SessionService } from './session-service'
import { keywordDefaults, keywordResolvers, keywordMutations } from '../graphql/resolvers/keyword.resolvers'
import { documentDefaults, documentResolvers } from '../graphql/resolvers/document.resolvers'
import { postDefaults, postResolvers, postMutations } from 'src/graphql/resolvers/post.resolvers'
import { customerDefaults, customerResolvers, customerMutations } from 'src/graphql/resolvers/customer.resolvers'
import { submissionDefaults, submissionResolvers, submissionMutations } from 'src/graphql/resolvers/submission.resolvers'
import mapValuesDeep from 'map-values-deep'
import possibleTypes from './possibleTypes.json'

const isoDateRegex = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z/

export const _convertDate = (str: string): Date | any => {
  if (typeof str === 'string' && isoDateRegex.test(str))
    return new Date(str)
  else
    return str
}

@Injectable({providedIn: 'root'})
export class GraphqlService {
  private apollo: Apollo;
  private link: ApolloLink;
  private cache: InMemoryCache;

  constructor(
    apollo: Apollo,
    httpLink: HttpLink,
    private sessionService: SessionService,
  ) {
    this.apollo = apollo

    const xsrfTag = document.querySelector('meta[name=csrf-token]')
    const xsrfToken = xsrfTag && xsrfTag.getAttribute('content') // from rails

    this.cache = new InMemoryCache({
      // dataIdFromObject: () => // custom idGetter,
      addTypename: true,
      cacheRedirects: {},
      fragmentMatcher: new IntrospectionFragmentMatcher({
        introspectionQueryResultData: possibleTypes
      }),
    } as InMemoryCacheConfig)

    this.cache.writeData({
      data: {
        ...keywordDefaults,
        ...documentDefaults,
        ...postDefaults,
        ...customerDefaults,
        ...submissionDefaults,
      }
    })

    const http = httpLink.create({
      uri: '/graphql',
      headers: new HttpHeaders({
        'X-XSRF-TOKEN': xsrfToken
      }),
      withCredentials: true
    })

    // Error handling middleware for intercepting Graphql mutation operational errors as well as network errors
    const error = onError(({ graphQLErrors, networkError }) => {
      if (!environment.production && !environment.test) {
        if (graphQLErrors)
          console.log('graphqlErrors: ', graphQLErrors)
        if (networkError) {
          console.log('networkErrors ', networkError)
          this.sessionService.signedIn().then(signedIn => {
            if (!signedIn) {
              window.location.pathname = '/'
            }
          })
        }
      }
    })

    const dateTypeConversion = new ApolloLink((operation, forward) => {
      return forward(operation).map(response => ({ ...response, data: mapValuesDeep(response.data, _convertDate) }) )
    })

    this.link = ApolloLink.from([
      error,
      dateTypeConversion,
      http as ApolloLink,
    ])
  }

  create(): void {
    this.apollo.create({
      link: this.link,
      // use restore on the cache instead of initialState
      cache: this.cache, //: cache.restore(window.__APOLLO_CLIENT__),
      resolvers: {
        ...keywordResolvers,
        ...documentResolvers,
        ...postResolvers,
        ...submissionResolvers,
        ...customerResolvers,

        Mutation: {
          ...keywordMutations,
          ...postMutations,
          ...submissionMutations,
          ...customerMutations
        }
      }
      // ssrMode: true,
      // ssrForceFetchDelay: 100,
      // connectToDevTools: true,
      // queryDeduplication: true,
    })
  }
}
